import React, { useMemo } from "react";
import { toast } from "react-toastify";
import useSWR, { mutate } from "swr";
import {
  AppConstants,
  AppRouteUi,
  PlatformApi,
  topLoaderProgress,
} from "../../configs";
import { AxiosAuth } from "../../core";
import {
  ApiResponse,
  ApiSchemaAddReforwardingVendor,
  ApiSchemaCreateVendorCompany,
  ApiSchemaVendorData,
  ApiSchemaVendorCompanyData,
  Option,
  WithPagination,
} from "../../models";
import { OptionMapper, UtilsJQuery } from "../../utils";
import { ApiSchemaChangeCallForwardingOption, ApiSchemaChangeCallReForwardingOption } from "../../models/api/vendor";

type Props = {
  companyId: string;
  showAddCompanyModal?: boolean;
  showReforwardingVendorModal?: boolean;
  shouldVendorCompanyGetAllInvoke?: boolean;
};
type pageVendorProps = {
  pageSize: number;
  pageNo: number;
  searchText?: string;
  setLoaderProgress: any;
};
let pagedVendorCompanyUrl: string;
export function useFunctionalityPagedVendorCompany(props: pageVendorProps) {
  const { pageSize, pageNo, searchText, setLoaderProgress } = props;

  const vendorId = AppRouteUi.Vendors.Profile.useParam();
  pagedVendorCompanyUrl = PlatformApi.Vendors.GetCompanyById(
    vendorId,
    pageSize,
    pageNo,
    searchText
  );

  const { data: apiPagedVendorCompanyList } = useSWR<{
    data: ApiSchemaVendorCompanyData[];
    count: number;
  }>(
    pagedVendorCompanyUrl,
    async () => {
      setLoaderProgress(topLoaderProgress.start);

      try {
        const response = await AxiosAuth.get<
          ApiResponse<WithPagination<ApiSchemaVendorCompanyData[]>>
        >(pagedVendorCompanyUrl);
        setLoaderProgress(topLoaderProgress.complete);
        return {
          data: response.data.data.data,
          count: response.data.data.count,
        };
      } catch (e) {
        setLoaderProgress(topLoaderProgress.complete);
        // toast.error("Failed to load vendor data.");
        throw e;
      }
    }
  );

  const pagedVendorCompanyList = useMemo(() => {
    return apiPagedVendorCompanyList?.data ?? [];
  }, [apiPagedVendorCompanyList]);

  const pagedVendorCompanyListCount = apiPagedVendorCompanyList?.count ?? 0;

  return {
    pagedVendorCompanyList,
    pagedVendorCompanyListCount,
    pagedVendorCompanyUrl,
  };
}
export function useFunctionalityVendorCompany(props: Props) {
  const {
    companyId,
    showAddCompanyModal,
    showReforwardingVendorModal,
    shouldVendorCompanyGetAllInvoke = false,
  } = props;
  const vendorId = AppRouteUi.Vendors.Profile.useParam();
  const vendorCompanyUrl = PlatformApi.Vendors.GetAllCompanyById(vendorId);
  const companyWithoutVendorUrl =
    PlatformApi.Vendors.GetCompanyWithoutVendor(companyId);
  const reforwardingVendorUrl =
    PlatformApi.Vendors.GetVendorsWithPhoneNumberOverFlow(companyId);

  const { data: apiCompanyListWithoutVendor } = useSWR<
    ApiSchemaVendorCompanyData[]
  >(
    showAddCompanyModal ? companyWithoutVendorUrl : null,
    () =>
      AxiosAuth.get<ApiResponse<WithPagination<ApiSchemaVendorCompanyData[]>>>(
        companyWithoutVendorUrl
      )
        .then((r) => {
          return r.data.data.data;
        })
        .catch((e) => {
          console.log(e);
          return e;
        }),
    {
      revalidateOnMount: true,
    }
  );

  const {
    data: apiReforwardingVendorList,
    isValidating: isReforwardingVendorLoading,
  } = useSWR<ApiSchemaVendorData[]>(
    showReforwardingVendorModal ? reforwardingVendorUrl : null,
    () =>
      AxiosAuth.get<ApiResponse<ApiSchemaVendorData[] | WithPagination<ApiSchemaVendorData[]>>>(
        reforwardingVendorUrl
      )
        .then((r) => {
          const responseData = r.data.data;
          if (Array.isArray(responseData)) {
            return responseData;
          }

          return responseData?.data ?? [];
        })
        .catch((e) => {
          console.error(e);
          return e;
        }),
    {
      revalidateOnMount: true,
    }
  );

  const companyListWithoutVendor = React.useMemo(() => {
    return Array.isArray(apiCompanyListWithoutVendor)
      ? OptionMapper.mapperFunc(apiCompanyListWithoutVendor)
      : [];
  }, [apiCompanyListWithoutVendor]);

  const reforwardingVendorList = React.useMemo(() => {
    return Array.isArray(apiReforwardingVendorList)
      ? OptionMapper.mapperFunc(apiReforwardingVendorList)
      : ([] as Option[]);
  }, [apiReforwardingVendorList]);

  const { data: apiVendorCompanyData } = useSWR<ApiSchemaVendorCompanyData[]>(
    shouldVendorCompanyGetAllInvoke ? vendorCompanyUrl : null,
    () =>
      AxiosAuth.get<ApiResponse<WithPagination<ApiSchemaVendorCompanyData[]>>>(
        vendorCompanyUrl
      )
        .then((r) => {
          return r.data.data.data;
        })
        .catch((e) => {
          console.error(e);
          return e;
        })
  );

  const vendorCompanyList = React.useMemo(() => {
    return Array.isArray(apiVendorCompanyData) ? apiVendorCompanyData : [];
  }, [apiVendorCompanyData]);

  const onDelete = React.useCallback(async (companyId: string) => {
    const btnLoading = UtilsJQuery.Ladda(".user-from-submit-btn");
    btnLoading.start?.();
    try {
      const res = await AxiosAuth.delete<ApiSchemaCreateVendorCompany>(
        PlatformApi.Vendors.DeleteCompany(vendorId, companyId)
      );
      if (res.status === AppConstants.Api.StatusOK) {
        toast.success("Company deleted successfully");
      }
    } catch (e) {
      toast.error("Company delete failed");
    }

    btnLoading.stop?.();
    return Promise.resolve(null);
  }, []);

  const onChangeCallForwardingOption = React.useCallback(
    async (data: ApiSchemaChangeCallForwardingOption) => {
      try {
        const res = await AxiosAuth.post<ApiSchemaChangeCallForwardingOption>(
          PlatformApi.Vendors.ChangeCallForwardingOption(
            data.companyId,
            data.isCallForwardingEnabled
          )
        );
        if (res.status === AppConstants.Api.StatusOK) {
          mutate(vendorCompanyUrl);
          toast.success("Call forwarding status updated");
        }
      } catch (e) {
        toast.error("Call forwarding status update failed");
      }

      return Promise.resolve(null);
    },
    []
  );

  const onChangeCallReForwardingOption = React.useCallback(
    async (data: ApiSchemaChangeCallReForwardingOption) => {
      try {
        const res = await AxiosAuth.post<ApiSchemaChangeCallReForwardingOption>(
          PlatformApi.Vendors.ChangeCallReForwardingOption(
            data.vendorId,
            data.companyId,
            data.isCallReForwardingEnabled
          )
        );
        if (res.status === AppConstants.Api.StatusOK) {
          mutate(vendorCompanyUrl);
          toast.success("Call Reforwarding status updated");
        }
      } catch (e) {
        toast.error("Call Reforwarding status update failed");
        throw e;
      }

      return Promise.resolve(null);
    },
    []
  );

  const onAddReforwardingVendor = React.useCallback(
    async (data: ApiSchemaAddReforwardingVendor) => {
      const btnLoading = UtilsJQuery.Ladda(".reforwarding-vendor-submit-btn");
      btnLoading.start?.();
      try {
        const res = await AxiosAuth.post<ApiSchemaAddReforwardingVendor>(
          PlatformApi.Vendors.AddReforwardingVendor(
            data.companyId,
            data.vendorId,
            data.reforwardingVendorId
          )
        );
        if (res.status === AppConstants.Api.StatusOK) {
          toast.success("Reforwarding vendor updated");
        }
      } catch (e) {
        toast.error("Reforwarding vendor update failed");
        throw e;
      } finally {
        btnLoading.stop?.();
      }

      return Promise.resolve(null);
    },
    []
  );

  const onAddCompany = React.useCallback(
    async (form: ApiSchemaCreateVendorCompany) => {
      const btnLoading = UtilsJQuery.Ladda(".login-from-submit-btn");
      btnLoading.start?.();
      try {
        const res = await AxiosAuth.post<ApiSchemaCreateVendorCompany>(
          PlatformApi.Vendors.CreateCompany(vendorId, form.companyId)
        );
        if (res.status === AppConstants.Api.StatusOK) {
          toast.success("Company added successfully");
        }
      } catch (e) {
        toast.error("Adding company failed");
      }

      btnLoading.stop?.();
      return Promise.resolve(null);
    },
    []
  );

  return {
    vendorCompanyList,
    companyListWithoutVendor,
    onAddCompany,
    onDelete,
    onChangeCallForwardingOption,
    onChangeCallReForwardingOption,
    onAddReforwardingVendor,
    companyWithoutVendorUrl,
    reforwardingVendorList,
    isReforwardingVendorLoading,
  };
}
